import { ApexOptions } from "apexcharts"
import { useMemo } from "react"
import ReactApexChart from "react-apexcharts"
import { MONTH_DAY_DATETIME } from "../../../chartDateAxis"

/*
    Conversion Trend + Which Ads Were Running (MPN-1530 §6.4).

    Two charts on one shared date axis: daily conversions above, and a bar per ad spanning
    the days it was scheduled below.

    Explicitly NOT attribution. It puts two existing datasets side by side so a reader can
    see for themselves whether a rise in conversions coincided with an ad going live. The
    causal-looking version of this question is MPN-1531, which is labelled "conversions
    during flight" precisely because coincidence is all either of them can show.

    Drawn as two Apex charts rather than one mixed chart because the Gantt needs a
    rangeBar and the trend needs a bar, and Apex will not mix those in one plot. They share
    an explicit min and max on the x axis, which is what keeps the columns lined up.
*/

type Span = { start: string; end: string }
type AdSpans = { ad_id: number; ad_name: string; spans: Span[]; days_scheduled: number }

const toMs = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number)
  return new Date(y, m - 1, d).getTime()
}

/*
    The two charts are separate Apex instances stacked vertically, so they line up only
    if their plot areas begin at the same x.

    Matching min and max is not enough, and the comment that used to say it was is what
    hid this: min/max align the DATA domain, not the pixel geometry. The gantt reserves
    room on the left for ad names, the trend only for a few digits, so the same date
    landed at a different pixel in each. Rachel saw a conversion spike sitting days away
    from the ad that was running under it.

    An identical fixed gutter on both axes is what actually aligns them. Fixed rather
    than measured because a name-dependent width would drift as soon as the ads change.
*/
const GUTTER = { labels: { minWidth: 190, maxWidth: 190 } }

const COLORS = ["#6366f1", "#0ea5e9", "#22c55e", "#f59e0b", "#ec4899", "#8b5cf6"]

export default function AdFlightChart({ data }: { data: any }) {
  const built = useMemo(() => {
    const conversionPoints: [number, number][] =
      data?.["conversions-by-day"]?.[0]?.data ?? []
    const ads: AdSpans[] = data?.["ad-schedule-spans"] ?? []

    if (conversionPoints.length === 0) return null

    const min = conversionPoints[0][0]
    const max = conversionPoints[conversionPoints.length - 1][0]

    /*
        Trailing zero days become nulls so the line simply stops.

        The series is zero-filled to the end of the window, and the last day is usually
        today, which has not finished. As columns that was invisible -- a zero-height bar
        draws nothing. As an area it is a cliff to the axis, which reads as "conversions
        collapsed" rather than "the day is not over".

        The POINTS stay, only their values go null: min and max are taken from the ends of
        this array and the gantt below shares them, so dropping the entries outright would
        shorten the axis and pull the two charts back out of alignment.
    */
    const trimmed: [number, number | null][] = conversionPoints.map((p) => [p[0], p[1]])
    for (let i = trimmed.length - 1; i >= 0 && !trimmed[i][1]; i--) trimmed[i] = [trimmed[i][0], null]

    const trendOptions: ApexOptions = {
      chart: {
        id: "flight-trend",
        /*
            An area, not a bar column, and this is what actually aligns the overlay.

            Equal plot boxes were not enough. A datetime BAR chart insets its columns by
            half a bar width so each column sits centred on its day, while the rangeBar
            below spans edge to edge. Same box, same min and max, different mapping: 7/23
            landed 17px apart in the two charts, which is precisely the "dates in the
            overlay are slightly off" Rachel reported.

            A line/area plots each point on its exact timestamp, so the two share one
            mapping by construction rather than by a padding constant that would drift
            the next time the bar width or the day count changed. §6.4 says "bar or line".
        */
        type: "area",
        height: 220,
        toolbar: { show: false },
        animations: { enabled: false },
        zoom: { enabled: false },
      },
      colors: ["#6366f1"],
      stroke: { width: 2, curve: "straight" },
      fill: { type: "gradient", gradient: { opacityFrom: 0.35, opacityTo: 0.05 } },
      markers: { size: 0, hover: { size: 4 } },
      dataLabels: { enabled: false },
      grid: { borderColor: "#f1f1f1", padding: { left: 10, right: 10 } },
      xaxis: {
        type: "datetime", min, max,
        // Same tick count on both, so the two axes print the same dates. The plot areas
        // already align to the pixel; without this Apex still samples ticks per chart
        // and the labels disagree (8/22 above, 8/20 below), which reads as a mismatch
        // even when the geometry is right.
        tickAmount: 10,
        labels: { datetimeUTC: false, datetimeFormatter: MONTH_DAY_DATETIME },
      },
      yaxis: {
        // Spread INSIDE labels, not beside it: a second `labels` key at this level
        // silently replaces the formatter and the axis loses its numbers.
        labels: { formatter: (v: number) => `${Math.round(v)}`, ...GUTTER.labels },
      },
      tooltip: { x: { format: "d MMM yyyy" } },
    }

    /*
        One rangeBar series per ad rather than one series with many points: Apex colours a
        rangeBar by series, and an ad with two runs must be one colour across both. Each
        ad occupies its own y category, so the rows stack rather than overlap.
    */
    /*
        Only ads that were actually live inside the visible window. Rachel: "Hide ads
        that weren't running in the timeframe selected via the date filter."

        Overlap, not containment: an ad that started in June and is still running today
        is very much running during an August window, and testing its start date alone
        would drop exactly the long-running ads that matter most.
    */
    const overlapsWindow = (ad: AdSpans) =>
      ad.spans.some((span) => toMs(span.start) <= max && toMs(span.end) + 86_400_000 >= min)

    const spanSeries = ads
      .filter((ad) => ad.spans.length > 0)
      .filter(overlapsWindow)
      .map((ad, index) => ({
        name: ad.ad_name,
        color: COLORS[index % COLORS.length],
        data: ad.spans.map((span) => ({
          x: ad.ad_name,
          // +1 day on the end so an inclusive last day is drawn as a full day rather
          // than as a zero-width sliver.
          y: [toMs(span.start), toMs(span.end) + 86_400_000],
        })),
      }))

    const ganttOptions: ApexOptions = {
      chart: {
        id: "flight-gantt",
        type: "rangeBar",
        height: Math.max(120, spanSeries.length * 46),
        toolbar: { show: false },
        animations: { enabled: false },
        zoom: { enabled: false },
      },
      plotOptions: { bar: { horizontal: true, barHeight: "55%", rangeBarGroupRows: true } },
      dataLabels: { enabled: false },
      grid: { borderColor: "#f1f1f1", padding: { left: 10, right: 10 } },
      // Same min and max as the trend above, which is what aligns the two axes.
      xaxis: {
        type: "datetime", min, max,
        // Same tick count on both, so the two axes print the same dates. The plot areas
        // already align to the pixel; without this Apex still samples ticks per chart
        // and the labels disagree (8/22 above, 8/20 below), which reads as a mismatch
        // even when the geometry is right.
        tickAmount: 10,
        labels: { datetimeUTC: false, datetimeFormatter: MONTH_DAY_DATETIME },
      },
      yaxis: GUTTER,
      legend: { show: false },
      tooltip: { x: { format: "d MMM yyyy" } },
    }

    return {
      trendOptions,
      ganttOptions,
      trendSeries: [{ name: "Conversions", data: trimmed }],
      spanSeries,
    }
  }, [data])

  if (!built) {
    return <p className="text-muted fs-11 mb-0">No conversions in this date range.</p>
  }

  return (
    <div>
      <ReactApexChart
        options={built.trendOptions}
        series={built.trendSeries as any}
        type="area"
        height={220}
      />
      {built.spanSeries.length === 0 ? (
        <p className="text-muted fs-11 mb-0">
          No ad has a schedule yet, so there are no flights to line up against the trend.
        </p>
      ) : (
        <>
          <div className="mon-subhead">Which ads were running</div>
          <ReactApexChart
            options={built.ganttOptions}
            series={built.spanSeries as any}
            type="rangeBar"
            height={Math.max(120, built.spanSeries.length * 46)}
          />
        </>
      )}
      <p className="mon-panel__foot">
        A visual aid only. An ad running while conversions rose is a coincidence in time,
        not evidence it caused them.
      </p>
    </div>
  )
}
